import { useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    makeStyles,
    Text,
} from '@fluentui/react-components';
import { ArrowReset20Regular, Delete20Regular, DeleteRegular } from '@fluentui/react-icons';
import { Case, UserAccount } from '../../../../data/types';
import { Column, Row } from '../../../common';
import {
    CustomDataTable,
    CustomDataTableColumn,
    CustomTableCellLayout,
} from '../../../controls/CustomDataTable/CustomDataTable';
import { useCustomDataTableFeatures } from '../../../controls/CustomDataTable/useCustomDataTableFeatures';
import { ConfirmationDialog } from '../../../dialogs/ConfirmationDialog';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { useUtilities } from '../../../../utilities/useUtilities';
import { CaseControl } from '../../../controls/CaseControl';
import { PartyControl } from '../../../controls/PartyControl';
import { useMediaSizes } from '../../../common/Styles/useMediaSizes';
import { appTokens } from '../../../common/Styles/appTokens';

type CasesPurgeRestoreDialogProps = {
    open: boolean;
    onOpenChange: (isOpen: boolean) => void;
    deletedCases: Case[];
    onRestoreCases: (cases: Case[]) => Promise<void>;
    onPurgeCases: (cases: Case[]) => Promise<void>;
    showClientIcon?: boolean;
    showCaseType?: boolean;
    showClient?: boolean;
    showDeletedBy?: boolean;
    showDeletedOn?: boolean;
};

enum ColumnName {
    clientIcon = 'clientIcon',
    caseName = 'caseName',
    caseType = 'caseType',
    retainedBy = 'retainedBy',
    deletedBy = 'deletedBy',
    deletedOn = 'deletedOn',
}

const useStyles = makeStyles({
    surface: {
        width: 'min(95vw, 980px)',
        maxWidth: '980px',
    },
    tableWrap: {
        height: '28rem',
    },
});

export const CasesPurgeRestoreDialog = (props: CasesPurgeRestoreDialogProps) => {
    const {
        open,
        onOpenChange,
        deletedCases,
        onRestoreCases,
        onPurgeCases,
        showClientIcon = false,
        showCaseType = false,
        showClient = false,
        showDeletedBy = false,
        showDeletedOn = false,
    } = props;

    // Hooks
    const { primaryAccount } = useAccountState();
    const { accountUsers } = (primaryAccount as UserAccount) ?? {};
    const utils = useUtilities();
    const tableFeatures = useCustomDataTableFeatures();
    const mediaSizes = useMediaSizes(); 

    // State
    const [selectedCases, setSelectedCases] = useState<Case[]>([]);
    const [purgingCaseIds, setPurgingCaseIds] = useState<Set<string>>(new Set());

    // Styles
    const styles = useStyles();

    const visibleDeletedCases = deletedCases.filter(
        (caseObj) => !caseObj.caseId || !purgingCaseIds.has(caseObj.caseId),
    );

    const columns = useMemo<CustomDataTableColumn<Case>[]>(() => {
        return [
            {
                id: ColumnName.clientIcon,
                header: '',
                renderCell: (caseObj) => {
                    return (
                        <>
                            <PartyControl size={'icon'} party={caseObj?.retainedBy?.party} />
                        </>
                    );
                },
                show: showClientIcon,
                pointerEvents: true,
                defaultWidth: 35,
                minWidth: 35,
            },
            {
                id: ColumnName.caseName,
                header: 'Case Name',
                compare: (a, b) => a.caseName?.localeCompare(b.caseName || '') || 0,
                renderCell: (caseObj) => {
                    return (
                        <>
                            {mediaSizes.isMobile ? (
                                <Text>{caseObj.caseName}</Text>
                            ) : (
                                <>
                                    <CaseControl caseObj={caseObj} size='extra-small' />
                                </>
                            )}
                        </>
                    );
                },
                defaultWidth: 200,
                minWidth: 100,
            },
            {
                id: ColumnName.caseType,
                header: 'Case Type',
                compare: (a, b) => a.caseType?.localeCompare(b.caseType || '') || 0,
                renderCell: (caseObj) => {
                    return (
                        <>
                            <Text>{caseObj.caseType || '-'}</Text>
                        </>
                    );
                },
                show: showCaseType,
                defaultWidth: 160,
                minWidth: 110,
            },
            {
                id: ColumnName.retainedBy,
                header: 'Client',
                compare: (a, b) => a.retainedBy?.party?.name?.localeCompare(b.retainedBy?.party?.name || '') || 0,
                renderCell: (caseObj) => {
                    return (
                        <>
                            <Text>{caseObj.retainedBy?.party?.name || '-'}</Text>
                        </>
                    );
                },
                show: showClient,
                defaultWidth: 220,
                minWidth: 130,
            },
            {
                id: ColumnName.deletedBy,
                header: 'Deleted By',
                compare: (a, b) => {
                    const aUser = utils.getUsersName(a.lifecycle?.deletedBy || '', accountUsers);
                    const bUser = utils.getUsersName(b.lifecycle?.deletedBy || '', accountUsers);
                    return aUser.localeCompare(bUser);
                },
                renderCell: (caseObj) => {
                    return (
                        <>
                            <Text>{utils.getUsersName(caseObj.lifecycle?.deletedBy || '', accountUsers) || '-'}</Text>
                        </>
                    );
                },
                defaultWidth: 180,
                minWidth: 120,
                show: showDeletedBy,
            },
            {
                id: ColumnName.deletedOn,
                header: 'Deleted On',
                compare: (a, b) => {
                    const aVal = new Date(a.lifecycle?.deletedOn || 0).valueOf();
                    const bVal = new Date(b.lifecycle?.deletedOn || 0).valueOf();
                    return aVal - bVal;
                },
                renderCell: (caseObj) => {
                    return (
                        <>
                            <Text>
                                {caseObj.lifecycle?.deletedOn ? new Date(caseObj.lifecycle.deletedOn).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                            </Text>
                        </>
                    );
                },
                defaultWidth: 180,
                minWidth: 140,
                show: showDeletedOn,
            },
        ];
    }, [accountUsers, utils]);

    const clearSelection = () => {
        setSelectedCases([]);
        tableFeatures.clearSelection();
    };

    const onConfirmRestore = async () => {
        if (!selectedCases.length) {
            return;
        }

        await onRestoreCases(selectedCases);
        clearSelection();
    };

    const onConfirmPurge = async () => {
        if (!selectedCases.length) {
            return;
        }

        const casesToPurge = selectedCases;
        const caseIdsToPurge = casesToPurge
            .map((caseObj) => caseObj.caseId)
            .filter((caseId): caseId is string => !!caseId);

        setPurgingCaseIds((current) => new Set([...current, ...caseIdsToPurge]));
        clearSelection();

        try {
            await onPurgeCases(casesToPurge);
        } catch {
            setPurgingCaseIds((current) => {
                const next = new Set(current);
                caseIdsToPurge.forEach((caseId) => next.delete(caseId));
                return next;
            });
        }
    };

    const onDialogOpenChange = (_ev: unknown, data: { open: boolean }) => {
        if (!data.open) {
            clearSelection();
        }

        onOpenChange(data.open);
    };

    return (
        <Dialog open={open} onOpenChange={onDialogOpenChange}>
            <DialogSurface className={styles.surface}>
                <DialogBody>
                    <DialogTitle>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                <Delete20Regular />
                            </Column>
                            <Column>Deleted Cases</Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        <Column gap='small'>
                            <Text>Select one or more deleted cases to restore them or permanently purge them.</Text>
                            <Text size={200}>{visibleDeletedCases.length} deleted cases</Text>
                            <Column className={styles.tableWrap}>
                                <CustomDataTable<Case>
                                    items={visibleDeletedCases}
                                    rowId='caseId'
                                    columns={columns}
                                    onSelectionChanged={setSelectedCases}
                                    showActions={false}
                                    bodyHeight='26rem'
                                    sortable
                                    {...tableFeatures.tableProps}
                                />
                            </Column>
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            appearance='primary'
                            icon={<ArrowReset20Regular />}
                            onClick={onConfirmRestore}
                            disabled={!selectedCases.length}
                            data-testid='cases-restore-selected-btn'
                        >
                            Restore Selected
                        </Button>
                        <ConfirmationDialog
                            title='Confirm Purge'
                            icon={<DeleteRegular />}
                            text={`Permanently purge ${selectedCases.length} selected case(s)? This action cannot be undone.`}
                            onConfirm={onConfirmPurge}
                            confirmButtonText='Purge'
                            cancelButtonText='Cancel'
                        >
                            <Button
                                appearance='primary'
                                style={{ backgroundColor: selectedCases.length ? appTokens.foregrounds.danger : undefined }}
                                icon={<DeleteRegular />}
                                disabled={!selectedCases.length}
                                data-testid='cases-purge-selected-btn'
                            >
                                Purge Selected
                            </Button>
                        </ConfirmationDialog>
                        <Button
                            appearance='secondary'
                            onClick={() => onOpenChange(false)}
                            data-testid='cases-close-dialog-btn'
                        >
                            Close
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
